import { useCallback, useState } from 'react';

import type { Question, QuestionnaireAnswers } from '@shared';

interface UseMultiSelectWithPrimaryQuestionOptions {
  question: Question;
  initialPrimary: string | null;
  initialSecondary: string[];
  initialPrimaryOtherText: string;
  initialSecondaryOtherText: string;
  onSubmit: (patch: Partial<QuestionnaireAnswers>) => Promise<void> | void;
}

export interface MultiSelectWithPrimaryQuestionState {
  primary: string | null;
  setPrimary: (next: string) => void;
  secondary: string[];
  setSecondary: (next: string[]) => void;
  primaryOtherText: string;
  setPrimaryOtherText: (next: string) => void;
  secondaryOtherText: string;
  setSecondaryOtherText: (next: string) => void;
  isValid: boolean;
  submit: () => Promise<void>;
}

export function useMultiSelectWithPrimaryQuestion({
  question,
  initialPrimary,
  initialSecondary,
  initialPrimaryOtherText,
  initialSecondaryOtherText,
  onSubmit,
}: UseMultiSelectWithPrimaryQuestionOptions): MultiSelectWithPrimaryQuestionState {
  const [primary, setPrimary] = useState<string | null>(initialPrimary);
  const [secondary, setSecondary] = useState<string[]>(initialSecondary);
  const [primaryOtherText, setPrimaryOtherText] = useState<string>(initialPrimaryOtherText);
  const [secondaryOtherText, setSecondaryOtherText] = useState<string>(initialSecondaryOtherText);

  const primaryNeedsOtherText = primary === 'other' && !primaryOtherText.trim();
  const secondaryNeedsOtherText =
    secondary.includes('other') && !secondaryOtherText.trim();
  const isValid = !!primary && !primaryNeedsOtherText && !secondaryNeedsOtherText;

  const submit = useCallback(async () => {
    if (!isValid || !primary) return;

    await onSubmit({
      [question.id]: primary,
      primary_goal_other_text: primary === 'other' ? primaryOtherText.trim() : undefined,
      secondary_goals: secondary.filter(v => v !== primary),
      secondary_goals_other_text: secondary.includes('other')
        ? secondaryOtherText.trim()
        : undefined,
    } as Partial<QuestionnaireAnswers>);
  }, [isValid, primary, primaryOtherText, secondary, secondaryOtherText, question.id, onSubmit]);

  return {
    primary,
    setPrimary,
    secondary,
    setSecondary,
    primaryOtherText,
    setPrimaryOtherText,
    secondaryOtherText,
    setSecondaryOtherText,
    isValid,
    submit,
  };
}
